/**
 * @fileoverview Pure helpers for mutating the inline CSS `style` string
 * we keep on TipTap table cells via the `PreserveStyles` extension.
 *
 * Why a separate module:
 *   - Cell border / background / vertical-align edits all touch the same
 *     `style` string and need to merge with each other (and with any
 *     styles the cell already carried, e.g. from an imported DOCX).
 *   - Border encoding has two valid shapes (`border:` shorthand vs the
 *     four `border-{side}:` long-hand). The shorthand is convenient for
 *     "all four sides equal" but breaks per-side toggling, so we always
 *     EXPAND on read and COLLAPSE on write to keep the user-visible
 *     style string minimal.
 *
 * Everything here is framework-free so it's easy to unit-test.
 */

const BORDER_SIDES = ["top", "right", "bottom", "left"] as const;
export type BorderSide = (typeof BORDER_SIDES)[number];

/** A parsed `style="..."` string as an ordered key→value map. */
export type StyleMap = Map<string, string>;

/**
 * Parse a CSS `style` attribute value into an ordered key→value map.
 * Empty / null inputs return an empty map. Unknown shorthand is preserved.
 */
export function parseStyle(value: string | null | undefined): StyleMap {
  const map: StyleMap = new Map();
  if (!value) return map;
  for (const decl of value.split(";")) {
    const trimmed = decl.trim();
    if (!trimmed) continue;
    const colon = trimmed.indexOf(":");
    if (colon === -1) continue;
    const prop = trimmed.slice(0, colon).trim().toLowerCase();
    const val = trimmed.slice(colon + 1).trim();
    if (!prop || !val) continue;
    map.set(prop, val);
  }
  return map;
}

/**
 * Serialize a StyleMap back to a `style` string, or `null` when empty
 * so the renderer can drop the attribute entirely.
 */
export function serializeStyle(map: StyleMap): string | null {
  if (map.size === 0) return null;
  const parts: string[] = [];
  for (const [prop, val] of map) {
    if (!val) continue;
    parts.push(`${prop}: ${val}`);
  }
  return parts.length === 0 ? null : parts.join("; ");
}

/**
 * If the map carries a `border:` shorthand, expand it into the four
 * `border-top/right/bottom/left:` long-hand entries and drop the
 * shorthand. Existing per-side entries WIN over the shorthand.
 *
 * This guarantees `setBorderSide` only ever has to look at the
 * long-hand keys.
 */
export function expandBorderShorthand(map: StyleMap): StyleMap {
  const shorthand = map.get("border");
  if (!shorthand) return map;
  for (const side of BORDER_SIDES) {
    const key = `border-${side}`;
    if (!map.has(key)) map.set(key, shorthand);
  }
  map.delete("border");
  return map;
}

/**
 * If the four `border-{side}:` values are all present AND identical,
 * collapse them back to a single `border:` shorthand. Keeps the
 * stored style strings short and matches the shape DOCX→HTML imports
 * produce, which is what the Word export pipeline expects.
 */
export function collapseBorderShorthand(map: StyleMap): StyleMap {
  const values = BORDER_SIDES.map((side) => map.get(`border-${side}`));
  const first = values[0];
  if (!first) return map;
  if (!values.every((v) => v === first)) return map;
  for (const side of BORDER_SIDES) map.delete(`border-${side}`);
  map.set("border", first);
  return map;
}

export interface BorderDecl {
  /** CSS length, e.g. `"0.5pt"`, `"1px"`. */
  width: string;
  /** `solid` / `dashed` / `dotted` / `none`. `none` removes the side. */
  style: "solid" | "dashed" | "dotted" | "double" | "none";
  /** Any valid CSS colour. Ignored when `style === "none"`. */
  color: string;
}

/**
 * Format a `BorderDecl` as a CSS shorthand value (e.g. `"0.5pt solid #000"`).
 */
export function formatBorderDecl(decl: BorderDecl): string {
  if (decl.style === "none") return "none";
  return `${decl.width} ${decl.style} ${decl.color}`;
}

/**
 * Set (or clear) a single border side on a style map. Handles the
 * shorthand expansion automatically.
 *
 * Pass `decl: null` or `decl.style === "none"` to remove the side
 * (this writes `border-{side}: none`, which is what altChunk + the
 * Word export's `mirrorBordersToMsoAlt` step expect).
 */
export function setBorderSide(
  map: StyleMap,
  side: BorderSide | "all",
  decl: BorderDecl | null,
): StyleMap {
  expandBorderShorthand(map);
  const targets = side === "all" ? BORDER_SIDES : [side];
  for (const s of targets) {
    const key = `border-${s}`;
    if (decl === null) {
      map.delete(key);
    } else {
      map.set(key, formatBorderDecl(decl));
    }
  }
  collapseBorderShorthand(map);
  return map;
}

/** Clear every border side (long-hand AND shorthand). */
export function clearAllBorders(map: StyleMap): StyleMap {
  map.delete("border");
  for (const side of BORDER_SIDES) map.delete(`border-${side}`);
  return map;
}

/**
 * Set (or clear) the cell background. Removes the legacy `background`
 * shorthand too so we don't leave both `background` and `background-color`
 * fighting each other.
 */
export function setBackground(map: StyleMap, color: string | null): StyleMap {
  map.delete("background");
  if (color === null) {
    map.delete("background-color");
  } else {
    map.set("background-color", color);
  }
  return map;
}

/**
 * Read the current cell background, checking both `background-color` and
 * the legacy `background` shorthand. Returns `null` if neither is set.
 */
export function readBackground(map: StyleMap): string | null {
  const bg = map.get("background-color");
  if (bg) return bg;
  const shorthand = map.get("background");
  if (!shorthand) return null;
  // `background: <color>` is the common case from DOCX → HTML imports.
  // We don't try to parse layered shorthand — practitioners rarely
  // author it and the worst case is the colour picker showing the
  // first token, which is still useful.
  return shorthand.trim().split(/\s+/)[0] ?? null;
}

export type VerticalAlign = "top" | "middle" | "bottom";

/** Set (or clear) the `vertical-align` cell property. */
export function setVerticalAlign(
  map: StyleMap,
  align: VerticalAlign | null,
): StyleMap {
  if (align === null) {
    map.delete("vertical-align");
  } else {
    map.set("vertical-align", align);
  }
  return map;
}

/**
 * Re-style every existing border side on a cell using a new `width`
 * and/or `color`, preserving the per-side present/absent shape and
 * the existing `style` keyword (`solid`, `dashed`, etc).
 *
 * This is what powers the "change width / colour of borders that are
 * already there" UX in the toolbar — picking a new value updates the
 * visible borders without forcing the user to re-pick a preset.
 *
 * Sides set to `none` (explicitly borderless) are left alone so a
 * preset like "Outer only" stays outer-only after a width tweak.
 *
 * Returns `true` when at least one border side was changed.
 */
export function restyleBorders(
  map: StyleMap,
  updates: { width?: string; color?: string },
): boolean {
  if (updates.width === undefined && updates.color === undefined) return false;
  expandBorderShorthand(map);
  const changedSides = BORDER_SIDES.filter((side) => {
    const current = map.get(`border-${side}`);
    if (!current) return false;
    if (current.trim().toLowerCase() === "none") return false;
    const parsed = parseBorderShorthand(current);
    if (!parsed) return false;
    const next: BorderDecl = {
      width: updates.width ?? parsed.width,
      style: parsed.style,
      color: updates.color ?? parsed.color,
    };
    map.set(`border-${side}`, formatBorderDecl(next));
    return true;
  });
  collapseBorderShorthand(map);
  return changedSides.length > 0;
}

/**
 * Read the dominant border declaration from a cell — used by the
 * toolbar to show the "current" colour and width. We prefer the
 * shorthand; otherwise we return the first long-hand side that's set
 * and not `none`. Returns `null` when the cell has no borders at all.
 */
export function readDominantBorder(map: StyleMap): BorderDecl | null {
  const shorthand = map.get("border");
  if (shorthand && shorthand.trim() !== "none") {
    const parsed = parseBorderShorthand(shorthand);
    if (parsed) return parsed;
  }
  for (const side of BORDER_SIDES) {
    const val = map.get(`border-${side}`);
    if (val && val.trim() !== "none") {
      const parsed = parseBorderShorthand(val);
      if (parsed) return parsed;
    }
  }
  return null;
}

/**
 * Best-effort parser for a CSS border shorthand. We accept the common
 * `<width> <style> <color>` order; anything more exotic returns null
 * and the caller falls back to the toolbar default.
 */
export function parseBorderShorthand(value: string): BorderDecl | null {
  const tokens = value.trim().split(/\s+/);
  if (tokens.length < 2) return null;
  let width: string | null = null;
  let style: BorderDecl["style"] | null = null;
  const colorParts: string[] = [];
  const STYLES = new Set(["solid", "dashed", "dotted", "double", "none"]);
  for (const token of tokens) {
    if (!style && STYLES.has(token.toLowerCase())) {
      style = token.toLowerCase() as BorderDecl["style"];
      continue;
    }
    if (!width && /^[\d.]+(?:px|pt|em|rem)?$/i.test(token)) {
      width = token;
      continue;
    }
    colorParts.push(token);
  }
  if (!style) return null;
  if (style === "none") {
    return { width: width ?? "0", style: "none", color: "transparent" };
  }
  if (!width) return null;
  const color = colorParts.join(" ").trim();
  if (!color) return null;
  return { width, style, color };
}
